import React, { useEffect, useRef, useState } from 'react';
import styles from './NotificationModal.module.scss';
import { createPortal } from 'react-dom';
import { phoneIconBlack, modal_background } from '../../assets/hostedassets';
import { NOTIFICATION_MODAL } from '../../utils/constants/commonConstants';

export type NotificationPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'center';

interface NotificationModalProps {
    isOpen: boolean;
    onClose?: () => void;
    header: string | React.ReactNode;
    content: string | React.ReactNode;
    footer?: React.ReactNode;
    position?: NotificationPosition;
    source?: NOTIFICATION_MODAL;
    customClassName?: string;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
    isOpen,
    onClose,
    header,
    content,
    footer,
    position,
    source = NOTIFICATION_MODAL.DEFAULT,
    customClassName,
}) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(isOpen);
    const [animationClass, setAnimationClass] = useState('');

    // Escape key handling
    useEffect(() => {
        if (isOpen && source !== NOTIFICATION_MODAL.CONTACT_US) {
            const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape') onClose?.();
            };
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen, onClose, source]);

    // Animation handling
    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            if (source === NOTIFICATION_MODAL.CONTACT_US) setAnimationClass(styles.slideIn);
        } else if (source === NOTIFICATION_MODAL.CONTACT_US) {
            setAnimationClass(styles.slideOut);
            const timer = setTimeout(() => setIsVisible(false), 400);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [isOpen, source]);

    if (!isVisible) return null;

    const handleClose = () => {
        if (source === NOTIFICATION_MODAL.CONTACT_US) {
            setAnimationClass(styles.slideOut);
            setTimeout(() => onClose?.(), 400);
        } else {
            onClose?.();
        }
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) handleClose();
    };

    return createPortal(
        <>
            <div className={styles.Backdrop} onClick={handleBackdropClick} />
            <div
                className={[
                    styles.NotificationModalContainer,
                    customClassName,
                    source === NOTIFICATION_MODAL.CONTACT_US ? animationClass : position ? styles[position.replaceAll('-', '_')] : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <div
                    className={styles.NotificationModal}
                    style={{ backgroundImage: `url(${modal_background})` }}
                    ref={modalRef}
                    role="dialog"
                    aria-modal="true"
                    tabIndex={-1}
                >
                    <button className={styles.CloseButton} onClick={handleClose} aria-label="Close">
                        &times;
                    </button>

                    <div className={styles.HeaderContainer}>
                        <img src={phoneIconBlack} alt="Phone" className={styles.PhoneIcon} />
                        <div className={styles.HeaderText}>{header}</div>
                    </div>

                    <div className={styles.Content}>{content}</div>
                    {footer && <div className={styles.Footer}>{footer}</div>}
                </div>
            </div>
        </>,
        document.body,
    );
};

export default NotificationModal;
